import { Table, Form, InputGroup, Button } from "react-bootstrap";
import { useState, useEffect, useCallback, useRef } from "react";
import "../../../../common/styles/table.css";
import ClassroomTableRow from "./ClassroomTableRow";
import { useGetAllclassrooms } from "../../../../hooks/teacher/classroom/useGetAllClassrooms";
import {
  ITEMS_PER_PAGE,
  SEARCH_DEBOUNCE_TIME,
} from "../../../../constant/constant";
import TablePagination from "../pagination/TablePagination";
import TableSkeleton from "../../common/TableSkeleton";
import TableError from "../../common/TableError";
import RefreshButtonTable from "../../../common/RefreshButtonTable";
import RoleGuard from "../../../auth/RoleGuard";
import { useGetTeachers } from "../../../../hooks/school/teacher/useGetTeachers";
import { useDebounce } from "../../../../utils/useDebounce";


/**
 * @param {Object} props
 * @param {string} [props.teacherId] - Optional teacher ID to filter classrooms
 * @param {string} [props.type] - Type of user (school-admin, teacher-admin)
 */
const ClassroomTable = ({ teacherId = "", type = "teacher-admin" }) => {
  const { loading, error, classrooms, meta, getAllClassrooms } =
    useGetAllclassrooms();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimeoutRef = useRef(null);

  const isSchoolAdmin = type === "school-admin";
  const {
    loading: teacherLoading,
    teachers,
    getTeachers,
    metaData: teacherMeta,
  } = useGetTeachers();

  const [teacherSearch, setTeacherSearch] = useState("");
  const debouncedTeacherSearch = useDebounce(teacherSearch, 400);
  const [teacherDropdownOpen, setTeacherDropdownOpen] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [teacherPage, setTeacherPage] = useState(1);
  const [teacherHasInit, setTeacherHasInit] = useState(false);
  const teacherDropdownRef = useRef(null);
  const teacherObserverTarget = useRef(null);

  // If a teacher is selected via the dropdown, use thatotherwise fall back to the prop
  const effectiveTeacherId = selectedTeacher
    ? String(selectedTeacher.id)
    : teacherId;


  /** Initial teacher fetch */
  useEffect(() => {
    if (isSchoolAdmin && !teacherHasInit) {
      getTeachers(1, ITEMS_PER_PAGE, false, "");
      setTeacherHasInit(true);
    }
  }, [isSchoolAdmin, teacherHasInit]);

  /** Re-fetch teachers when search changes */
  useEffect(() => {
    if (!isSchoolAdmin || !teacherHasInit) return;
    setTeacherPage(1);
    getTeachers(1, ITEMS_PER_PAGE, false, debouncedTeacherSearch);
  }, [debouncedTeacherSearch]);

  /** Infinite scroll for teacher dropdown */
  useEffect(() => {
    if (!isSchoolAdmin) return;
    const target = teacherObserverTarget.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          !teacherLoading &&
          teacherMeta &&
          teacherMeta.current_page < teacherMeta.last_page
        ) {
          const nextPage = teacherPage + 1;
          setTeacherPage(nextPage);
          getTeachers(nextPage, ITEMS_PER_PAGE, true, debouncedTeacherSearch);
        }
      },
      { threshold: 0.1, rootMargin: "50px" },
    );

    observer.observe(target);
    return () => observer.unobserve(target);
  }, [
    isSchoolAdmin,
    teacherLoading,
    teacherMeta,
    teacherPage,
    debouncedTeacherSearch,
  ]);

  /** Close dropdown on outside click */
  useEffect(() => {
    if (!isSchoolAdmin) return;
    const handleClickOutside = (e) => {
      if (
        teacherDropdownRef.current &&
        !teacherDropdownRef.current.contains(e.target)
      ) {
        setTeacherDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSchoolAdmin]);

  /** Select a teacher from the dropdown */
  const handleSelectTeacher = (teacher) => {
    setSelectedTeacher(teacher);
    setTeacherDropdownOpen(false);
    setTeacherSearch("");
    setCurrentPage(1);
    setDebouncedSearch("");
    setSearchTerm("");
  };

  /** Clear selected teacher filter */
  const handleClearTeacher = () => {
    setSelectedTeacher(null);
    setCurrentPage(1);
    setDebouncedSearch("");
    setSearchTerm("");
  };

  const teacherHasMore =
    teacherMeta && teacherMeta.current_page < teacherMeta.last_page;

  /**
   * Build the filters object for the API call.
   */
  const buildFilters = useCallback((search) => {
    if (!search || search.trim() === "") return {};
    return { name: search.trim() };
  }, []);

  /**
   * Fetch classrooms with current page and search filters
   */
  const fetchClassrooms = useCallback(
    (page, search) => {
      getAllClassrooms({
        page,
        per_page: ITEMS_PER_PAGE,
        teacher_id: effectiveTeacherId,
        filters: buildFilters(search),
      });
    },
    [buildFilters, effectiveTeacherId],
  );

  // Fetch classrooms on initial load and when page, search, or teacher filter changes
  useEffect(() => {
    fetchClassrooms(currentPage, debouncedSearch);
  }, [currentPage, debouncedSearch, effectiveTeacherId]);

  /**
   * Handle search input change with debounce
   */
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1); // Reset to first page on new search
      setDebouncedSearch(value);
    }, SEARCH_DEBOUNCE_TIME);
  };

  /**
   * Clear search input and reset results
   */
  const handleClearSearch = () => {
    setSearchTerm("");
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setCurrentPage(1);
    setDebouncedSearch("");
  };

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleRefresh = () => {
    fetchClassrooms(currentPage, debouncedSearch);
  };

  /**
   * Refresh after delete - handles the edge case when deleting
   * the last item on a page by going back to the previous page
   */
  const handleRefreshAfterDelete = () => {
    if (classrooms.length === 1 && currentPage > 1) {
      // Go back to previous page (useEffect will trigger the fetch)
      setCurrentPage(currentPage - 1);
    } else {
      // Stay on current page and refresh
      fetchClassrooms(currentPage, debouncedSearch);
    }
  };

  // Show skeleton only on initial load (when classrooms is undefined and loading)
  if (loading && classrooms === undefined) {
    return <TableSkeleton rows={ITEMS_PER_PAGE} columns={3} />;
  }

  if (error || classrooms === null) {
    return (
      <TableError
        error={error}
        handleRefresh={handleRefresh}
        loading={loading}
      />
    );
  }

  const totalPages = meta?.last_page || 1;
  const hasClassrooms = classrooms && classrooms.length > 0;
  const isSearching = debouncedSearch.trim() !== "";

  return (
    <div>
      {/* Search, Teacher Filter, and Refresh Row */}
      <div className="d-flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
        <InputGroup style={{ flex: "1 1 250px", maxWidth: 400 }}>
          <InputGroup.Text>
            <i className="bi bi-search"></i>
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={handleSearchChange}
            disabled={loading && !isSearching && classrooms === undefined}
            className="shadow-none"
          />
          {searchTerm && (
            <Button
              variant="outline-secondary"
              onClick={handleClearSearch}
              disabled={loading}
              title="Clear search"
            >
              <i className="bi bi-x-lg"></i>
            </Button>
          )}
        </InputGroup>


        <div className="d-flex align-items-center gap-2" style={{ flex: "1 1 auto", minWidth: 0 }}>
          {/* ─── Teacher Filter (school-admin only) ─── */}
          {isSchoolAdmin && (
            <div ref={teacherDropdownRef} style={{ position: "relative", flex: "1 1 180px", maxWidth: 280 }}>
              <InputGroup>
                <InputGroup.Text>
                  <i className="bi bi-person"></i>
                </InputGroup.Text>
                {selectedTeacher ? (
                  <>
                    <Form.Control
                      type="text"
                      readOnly
                      value={`${selectedTeacher.firstName || selectedTeacher.name} ${selectedTeacher.lastName || ""}`.trim()}
                      className="shadow-none"
                      style={{ cursor: "default", backgroundColor: "#f8f9fa" }}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={handleClearTeacher}
                      title="Clear teacher filter"
                    >
                      <i className="bi bi-x-lg"></i>
                    </Button>
                  </>
                ) : (
                  <Form.Control
                    type="text"
                    placeholder="Filter by teacher..."
                    value={teacherSearch}
                    onChange={(e) => {
                      setTeacherSearch(e.target.value);
                      if (!teacherDropdownOpen) setTeacherDropdownOpen(true);
                    }}
                    onFocus={() => setTeacherDropdownOpen(true)}
                    autoComplete="off"
                    className="shadow-none"
                  />
                )}
              </InputGroup>

              {/* Dropdown list */}
              {teacherDropdownOpen && !selectedTeacher && (
                <ul
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    background: "#fff",
                    border: "1px solid #dee2e6",
                    borderRadius: 8,
                    marginTop: 4,
                    maxHeight: 220,
                    overflowY: "auto",
                    zIndex: 1050,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    listStyle: "none",
                    padding: "4px 0",
                  }}
                >
                  {teachers && teachers.length > 0 ? (
                    <>
                      {teachers.map((teacher) => (
                        <li
                          key={teacher.id}
                          onClick={() => handleSelectTeacher(teacher)}
                          style={{
                            padding: "8px 14px",
                            cursor: "pointer",
                            fontSize: 14,
                            transition: "background 0.15s",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background = "#f0f4ff")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = "transparent")
                          }
                        >
                          <div style={{ fontWeight: 500, color: "#333" }}>
                            {teacher.firstName || teacher.name}{" "}
                            {teacher.lastName || ""}
                          </div>
                          {teacher.email && (
                            <div style={{ fontSize: 12, color: "#6c757d" }}>
                              {teacher.email}
                            </div>
                          )}
                        </li>
                      ))}

                      {/* Infinite scroll sentinel */}
                      {teacherHasMore && (
                        <li
                          ref={teacherObserverTarget}
                          style={{ textAlign: "center", padding: 8 }}
                        >
                          {teacherLoading && (
                            <div
                              className="spinner-border spinner-border-sm"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                          )}
                        </li>
                      )}
                    </>
                  ) : teacherLoading ? (
                    <li style={{ textAlign: "center", padding: 12 }}>
                      <div
                        className="spinner-border spinner-border-sm"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </li>
                  ) : (
                    <li
                      style={{
                        textAlign: "center",
                        padding: 12,
                        color: "#6c757d",
                        fontSize: 14,
                      }}
                    >
                      No teachers found
                    </li>
                  )}
                </ul>
              )}
            </div>
          )}

          <RefreshButtonTable handleRefresh={handleRefresh} loading={loading} />
        </div>
      </div>

      {!hasClassrooms ? (
        <div className="alert alert-info mt-3" role="alert">
          {isSearching
            ? `No classrooms found matching "${debouncedSearch}".`
            : selectedTeacher
              ? "No classrooms found for the selected teacher."
              : "No classrooms found."}
        </div>
      ) : (
        <div className="custom-table-container mt-3">
          <div className="custom-table-wrapper">
            <Table hover className="custom-table">
              <thead className="custom-table-head">
                <tr>
                  <th>Classrooms</th>
                  <th> Students Count</th>
                  <th> Lessons Count</th>
                  <RoleGuard permission="dashboard_edit_delete_classroom">
                    <th>Action</th>
                  </RoleGuard>
                </tr>
              </thead>
              <tbody>
                {classrooms.map((classroom) => (
                  <ClassroomTableRow
                    key={classroom.id}
                    classroom={classroom}
                    refetch={handleRefreshAfterDelete}
                    disabled={loading}
                    type={type}
                  />
                ))}
              </tbody>
            </Table>
          </div>

          <TablePagination
            totalPage={totalPages}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            disabled={loading}
          />
        </div>
      )}
    </div>
  );
};

export default ClassroomTable;

